/**
 * Internal call transfer — React Query hooks and the failure vocabulary.
 *
 * The roster is fetched when the panel opens, not on every render: it is one
 * query per open, and it is only meaningful while that call is live.
 */

import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import type { ApiError } from '@/lib/api/errors';
import { callTransferApi } from './transfer.api';
import type {
  TransferErrorCode,
  TransferFailure,
  TransferTargetRef,
  WarmTransferState,
} from './transfer.types';

export const transferKeys = {
  targets: (callControlId: string) => ['call-transfer', 'targets', callControlId] as const,
  warmStatus: (callControlId: string, startedAt: number) =>
    ['call-transfer', 'warm', callControlId, startedAt] as const,
};

/** How often an ask-first transfer is checked while it rings or is connected. */
export const WARM_TRANSFER_POLL_MS = 1000;

/** Outcomes: the transfer is over and there is nothing left to poll for. */
export const WARM_TRANSFER_FINAL_STATES: ReadonlySet<WarmTransferState> = new Set([
  'completed',
  'cancelled',
  'no_answer',
  'declined',
  'target_left',
  'caller_left',
  'handed_over',
  'failed',
]);

/** Who this call can go to. Enabled only while the panel is open. */
export function useTransferTargets(callControlId: string | null, enabled: boolean) {
  return useApiQuery({
    queryKey: transferKeys.targets(callControlId ?? ''),
    queryFn: () => callTransferApi.targets(callControlId as string),
    enabled: enabled && !!callControlId,
    // The panel says it in place; a toast on top would be noise.
    showErrorToast: false,
    // A roster is only true for as long as devices stay online.
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Perform the transfer. IRREVERSIBLE once it succeeds.
 *
 * The auto-toast is suppressed deliberately: these failures are not
 * interchangeable, and one of them must not be a toast at all.
 */
export function useTransferCall(callControlId: string | null) {
  return useApiMutation<Awaited<ReturnType<typeof callTransferApi.transfer>>, TransferTargetRef>({
    mutationFn: (target) => callTransferApi.transfer(callControlId as string, target),
    showErrorToast: false,
  });
}

/** Start an ask-first transfer. The agent stays on the call. */
export function useStartWarmTransfer(callControlId: string | null) {
  return useApiMutation<Awaited<ReturnType<typeof callTransferApi.transfer>>, TransferTargetRef>({
    mutationFn: (target) => callTransferApi.transfer(callControlId as string, target, 'warm'),
    showErrorToast: false,
  });
}

/**
 * Follow an ask-first transfer until it has an outcome. Polled, because the
 * moments that matter — the teammate picking up, declining, hanging up — happen
 * on the server and nothing pushes them to the browser.
 */
export function useWarmTransferStatus(
  callControlId: string | null,
  enabled: boolean,
  /** Which transfer on this call — a second one must not inherit the first's outcome. */
  startedAt = 0,
) {
  return useApiQuery({
    queryKey: transferKeys.warmStatus(callControlId ?? '', startedAt),
    queryFn: () => callTransferApi.warmStatus(callControlId as string),
    enabled: enabled && !!callControlId,
    showErrorToast: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) => {
      const state = query.state.data?.data?.state;
      return state && WARM_TRANSFER_FINAL_STATES.has(state) ? false : WARM_TRANSFER_POLL_MS;
    },
  });
}

/** Complete or cancel an ask-first transfer. */
export function useWarmTransferAction(
  callControlId: string | null,
  action: 'complete' | 'cancel',
) {
  return useApiMutation<Awaited<ReturnType<typeof callTransferApi.completeWarm>>, void>({
    mutationFn: () =>
      action === 'complete'
        ? callTransferApi.completeWarm(callControlId as string)
        : callTransferApi.cancelWarm(callControlId as string),
    showErrorToast: false,
  });
}

/** What the agent is told when an ask-first transfer ends without the handover. */
export function describeWarmOutcome(state: WarmTransferState, targetName: string): string | null {
  switch (state) {
    case 'no_answer':
      return `${targetName} didn't answer. You're back with the caller.`;
    case 'declined':
      return `${targetName} declined. You're back with the caller.`;
    case 'target_left':
      return `${targetName} hung up. You're back with the caller.`;
    case 'cancelled':
      return "Transfer cancelled. You're back with the caller.";
    case 'failed':
      return "The transfer couldn't be completed. You're back with the caller.";
    case 'caller_left':
      return 'The caller hung up while on hold.';
    default:
      return null;
  }
}

/** The CS's own code, when it survived the proxy. */
function codeOf(error: ApiError): TransferErrorCode | '' {
  const data = error.data as { code?: unknown } | undefined;
  return typeof data?.code === 'string' ? (data.code as TransferErrorCode) : '';
}

/**
 * Turn a failure into the words the agent needs, and say whether they still
 * have the caller.
 *
 * `callLost` is the whole point of this function. Every code except
 * TRANSFER_FAILED leaves the call up and the panel usable; TRANSFER_FAILED means
 * the agent's leg is gone and the customer is in voicemail, so there is nothing
 * to retry and the message has to outlive the in-call UI.
 */
export function describeTransferFailure(error: ApiError, targetName?: string): TransferFailure {
  const code = codeOf(error);
  const who = targetName || 'That teammate';

  switch (code) {
    case 'TARGET_UNREACHABLE':
      return { code, callLost: false, message: `${who} has no device online right now.` };
    case 'CALLER_GONE':
      return { code, callLost: false, message: 'The caller already hung up.' };
    case 'IN_VOICEMAIL':
      return { code, callLost: false, message: 'That caller is already recording a voicemail.' };
    case 'TRANSFER_IN_PROGRESS':
      return {
        code,
        callLost: false,
        message: 'Someone else is already transferring this call.',
      };
    case 'TARGET_NOT_FOR_NUMBER':
      // The handset IS online — it just does not answer this line. "No device
      // online" would send someone hunting a hardware fault that does not exist.
      return { code, callLost: false, message: `${who} doesn't answer calls to this number.` };
    case 'CALL_NOT_FOUND':
    case 'NOT_YOUR_CALL':
      return { code, callLost: false, message: 'That call is no longer active.' };
    case 'TRANSFER_NOT_STARTED':
      return {
        code,
        callLost: false,
        message: "The transfer couldn't be started. You're still on the call.",
      };
    case 'WARM_TRANSFER_UNAVAILABLE':
      return {
        code,
        callLost: false,
        message: 'Ask first isn\'t available on this call. Use Transfer now instead.',
      };
    case 'TARGET_NOT_CONNECTED':
      return { code, callLost: false, message: `${who} hasn't picked up yet.` };
    case 'INVALID_NUMBER':
    case 'NUMBER_NOT_ALLOWED':
      // The calling service's own words name the rule that was broken ("US or
      // Canadian numbers only", "full 10-digit number"); a generic line here
      // would leave the agent guessing what to fix.
      return {
        code,
        callLost: false,
        message: error.message || "That number can't be dialled.",
      };
    case 'DIRECTORY_ENTRY_NOT_FOUND':
      return {
        code,
        callLost: false,
        message: 'That saved number was removed. Pick another, or ask an admin to add it back.',
      };
    case 'NO_WARM_TRANSFER':
      return { code, callLost: false, message: 'That transfer has already ended.' };
    case 'NOT_YOUR_TRANSFER':
      return {
        code,
        callLost: false,
        message: 'Only the agent who started this transfer can finish it.',
      };
    case 'TRANSFER_FAILED':
      return {
        code,
        callLost: true,
        message: `The transfer didn't go through and your call has ended — ${
          targetName ? `${targetName} was never connected` : 'the teammate was never connected'
        }. The caller was moved to voicemail, so follow up with them directly.`,
      };
    default:
      break;
  }

  // An UNCODED 404 is not the same as CALL_NOT_FOUND, which is handled above.
  // The frontend and the operations service roll out separately, so a 404 with
  // no code is most likely the transfer route not being deployed yet — telling
  // the agent their caller is gone would be a lie, and an alarming one.
  if (error.status === 404) {
    return {
      code: '',
      callLost: false,
      message:
        "Transfer isn't available right now — your call is unaffected. Refresh and try again.",
    };
  }

  // A 5xx with no code of its own: something broke where we cannot read it.
  // Claiming either outcome would be a guess, and one of those guesses tells an
  // agent their customer is in voicemail when they may still be on the line.
  if (error.status >= 500) {
    return {
      code: '',
      callLost: false,
      message:
        "The calling service couldn't complete the transfer. Check whether you're still connected to the caller before calling back.",
    };
  }

  return { code: '', callLost: false, message: error.message || 'Could not transfer the call.' };
}
